import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import {
    selectFilteredCompanies,
    selectCompanies,
    selectLocations,
    selectAllPlatform,
    selectClients,
    selectAllBranchs,
    selectLoading,
    selectError,
    selectStatusFilter,
    setStatusFilter,
    fetchCompanyData
} from '../store/slices/companySlice';
import { saveCompany } from '../utilities/api';
import Compnaymodal from '../common/Compnaymodal';
import './CategoryMaster.css';

const emptyCompany = {
    company_id: null,
    company_name: '',
    phone_number: '',
    alernative_mobile: '',
    gst_no: '',
    company_address: '',
    compmay_status: 'Active',
    location_id: null,
    effective_from: new Date().toISOString().split('T')[0],
    data_transfer: 'pending',
    platform_id: null,
    branch_id: null,
    client_id: null
};

const CompanyContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const filteredCompanies = useSelector(selectFilteredCompanies);
    const companies = useSelector(selectCompanies);
    const clients = useSelector(selectClients);
    const allBranchs = useSelector(selectAllBranchs);
    const locations = useSelector(selectLocations);
    const allPlatform = useSelector(selectAllPlatform);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const statusFilter = useSelector(selectStatusFilter);

    const [globalFilterValue, setGlobalFilterValue] = useState('');

    useEffect(() => {
        if (error) {
            toast.current?.show({ severity: 'error', summary: 'Load Error', detail: error, life: 5000 });
        }
    }, [error]);
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(emptyCompany);

    const activeCount = useMemo(() => companies.filter((c) => c.compmay_status === 'Active').length, [companies]);
    const inactiveCount = useMemo(() => companies.filter((c) => c.compmay_status === 'Inactive').length, [companies]);

    const reload = () => {
        dispatch(
            fetchCompanyData({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    };

    const handleSaveRequest = (savedCompany) => {
        const isEdit = !!savedCompany.company_id;
        confirmDialog({
            message: isEdit ? `Are you sure you want to update "${savedCompany.company_name}"?` : `Are you sure you want to add "${savedCompany.company_name}"?`,
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(savedCompany)
        });
    };

    const handleConfirmedSave = async (savedCompany) => {
        try {
            const payload = { ...savedCompany, company_id: savedCompany.company_id || null };
            const resData = await saveCompany(payload);
            if (resData?.status) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: resData.message || 'Saved successfully', life: 3000 });
                setDisplayDialog(false);
                setSelectedCompany(emptyCompany);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: resData?.message || 'Save failed. Please try again.', life: 4000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Save failed. Please try again.', life: 4000 });
        }
    };

    const statusBodyTemplate = (rowData) => (
        <Tag value={rowData.compmay_status} severity={rowData.compmay_status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />
    );

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                setSelectedCompany({ ...rowData });
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Company List</h3>
                    <div className="category-status-tabs">
                        <button
                            className={`status-tab-btn active-tab${statusFilter === 'active' ? ' selected' : ''}`}
                            onClick={() => dispatch(setStatusFilter(statusFilter === 'active' ? 'all' : 'active'))}
                        >
                            <i className="pi pi-check-circle" />
                            Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button
                            className={`status-tab-btn inactive-tab${statusFilter === 'inactive' ? ' selected' : ''}`}
                            onClick={() => dispatch(setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive'))}
                        >
                            <i className="pi pi-times-circle" />
                            Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search companies..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        label="Add Company"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={() => {
                            setSelectedCompany({ ...emptyCompany });
                            setDisplayDialog(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                <DataTable
                    value={filteredCompanies}
                    filters={filters}
                    globalFilterFields={['company_name', 'phone_number', 'gst_no']}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                    showGridlines
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm sub-masters-table"
                    emptyMessage="No companies found"
                >
                    <Column field="company_id" header="ID" sortable style={{ width: '80px' }} />
                    <Column field="company_name" header="Company Name" sortable />
                    <Column field="phone_number" header="Phone" sortable style={{ width: '160px' }} />
                    <Column field="gst_no" header="GST No" sortable style={{ width: '180px' }} />
                    <Column field="compmay_status" header="Status" body={statusBodyTemplate} sortable style={{ width: '120px', textAlign: 'center' }} />
                    <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                </DataTable>
                </div>
            )}

            <Compnaymodal
                visible={displayDialog}
                onHide={() => {
                    setDisplayDialog(false);
                    setSelectedCompany({ ...emptyCompany });
                }}
                companyData={selectedCompany}
                onSave={handleSaveRequest}
                clients={clients}
                allBranchs={allBranchs}
                alllocation={locations}
                allplatform={allPlatform}
            />
        </Card>
    );
};

export default CompanyContent;
